import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { COMPANY, EMAILJS, CALENDLY_URL } from '../../data/siteData';
import SectionHeader from '../common/SectionHeader';
import Button from '../common/Button';

export default function ContactSection() {
  const formRef = useRef();
  const [form, setForm] = useState({ from_name: '', from_email: '', service: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.sendForm(EMAILJS.serviceId, EMAILJS.templateId, formRef.current, EMAILJS.publicKey);
      setStatus('success');
      setForm({ from_name: '', from_email: '', service: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(201,168,76,0.15)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)', fontSize: '14px',
    fontFamily: 'var(--font-sans)', outline: 'none',
    transition: 'border-color 0.2s',
    WebkitAppearance: 'none',
  };

  const labelStyle = {
    display: 'block', fontSize: '11px',
    letterSpacing: '0.07em', textTransform: 'uppercase',
    color: 'var(--text-secondary)', marginBottom: '8px',
  };

  return (
    <>
      {/* CTA Band */}
      <div style={{ background: 'var(--gold)', padding: '60px 20px', textAlign: 'center', width: '100%' }}>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(22px, 5vw, 48px)', color: 'var(--ink)', fontWeight: 700, marginBottom: '12px' }}>
          Ready to Start Building Wealth?
        </h2>
        <p style={{ fontSize: 'clamp(13px, 3vw, 16px)', color: 'rgba(13,15,20,0.6)', maxWidth: '460px', margin: '0 auto 28px', lineHeight: 1.65 }}>
          Speak to one of our advisors today. No jargon, no minimums — just expert guidance.
        </p>
        <div className="cta-btns">
          <Button variant="dark" href="#contact">Send a Message</Button>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'transparent', border: '2px solid rgba(13,15,20,0.3)',
            color: 'var(--ink)', fontFamily: 'var(--font-sans)',
            fontSize: '14px', fontWeight: 500, padding: '13px 28px',
            borderRadius: 'var(--radius-sm)', textDecoration: 'none',
          }}>
            📅 Book a Free Call
          </a>
        </div>
      </div>

      {/* Contact Section */}
      <section id="contact" className="section" style={{ background: 'var(--ink-soft)', width: '100%', overflow: 'hidden' }}>
        <div className="container">
          <div className="contact-grid">

            {/* Left */}
            <div>
              <SectionHeader tag="Contact Us" title='Let&apos;s <em style="color:var(--gold);font-style:normal">Talk</em>' />
              <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.85, fontWeight: 300, marginBottom: '28px' }}>
                Whether you're a first-time investor or an institution, we're here to help. An advisor will get back to you within 24 hours.
              </p>

              {/* Calendly block */}
              <div style={{
                background: 'rgba(201,168,76,0.06)',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 'var(--radius-md)',
                padding: '16px 20px', marginBottom: '28px',
              }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '4px' }}>
                  Prefer a live conversation?
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Book a free 30-min consultation
                </div>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: 'var(--gold)', color: 'var(--ink)',
                  fontFamily: 'var(--font-sans)', fontSize: '13px', fontWeight: 500,
                  padding: '10px 20px', borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                }}>
                  📅 Book a Call
                </a>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '4px' }}>Email</div>
                  <a href={`mailto:${COMPANY.email}`} style={{ fontSize: '15px', color: 'var(--text-primary)', textDecoration: 'none' }}>{COMPANY.email}</a>
                </div>
                <div>
                  <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '4px' }}>Region</div>
                  <span style={{ fontSize: '15px', color: 'var(--text-primary)' }}>{COMPANY.address}</span>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div style={{
              background: 'rgba(201,168,76,0.04)',
              border: '1px solid var(--gold-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '32px 24px',
            }}>
              {status === 'success' ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ fontSize: '40px', marginBottom: '16px' }}>✓</div>
                  <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', color: 'var(--gold)', marginBottom: '8px' }}>Message Sent!</h3>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px' }}>We'll get back to you within 24 hours.</p>
                  <Button variant="outline" onClick={() => setStatus('idle')}>Send Another</Button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={labelStyle}>Full Name</label>
                    <input style={inputStyle} type="text" name="from_name" placeholder="John Doe"
                      value={form.from_name} onChange={handleChange} required
                      onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email Address</label>
                    <input style={inputStyle} type="email" name="from_email" placeholder="you@example.com"
                      value={form.from_email} onChange={handleChange} required
                      onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'} />
                  </div>
                  <div>
                    <label style={labelStyle}>Service of Interest</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} name="service"
                      value={form.service} onChange={handleChange}
                      onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'}>
                      <option value="" style={{ background: '#141720' }}>Select a service...</option>
                      <option value="Investment Advisory" style={{ background: '#141720' }}>Investment Advisory</option>
                      <option value="Asset Management" style={{ background: '#141720' }}>Asset Management</option>
                      <option value="Crypto & Digital Assets" style={{ background: '#141720' }}>Crypto & Digital Assets</option>
                      <option value="Stocks & Equities" style={{ background: '#141720' }}>Stocks & Equities</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Message</label>
                    <textarea style={{ ...inputStyle, minHeight: '110px', resize: 'vertical' }}
                      name="message" placeholder="Tell us about your goals..."
                      value={form.message} onChange={handleChange}
                      onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'} />
                  </div>
                  {status === 'error' && (
                    <p style={{ fontSize: '13px', color: '#E24B4A', textAlign: 'center' }}>
                      Something went wrong. Email us at {COMPANY.email}
                    </p>
                  )}
                  <Button variant="primary" style={{ width: '100%', opacity: status === 'sending' ? 0.7 : 1 }}>
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
